import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import PipelineStatus from '../components/PipelineStatus';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const PublicPlantProfile = () => {
    const { id } = useParams();
    const [plant, setPlant] = useState(null);
    const [certificates, setCertificates] = useState([]);
    const [hourlyData, setHourlyData] = useState([]);
    const [rawRecords, setRawRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        let role = null;
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                role = payload.role;
                setUserRole(role);
            } catch (e) {}
        }

        const fetchProfile = async () => {
            try {
                const [plantRes, certRes, hourlyRes] = await Promise.all([
                    api.get(`/api/plants/${id}`),
                    api.get(`/api/plants/${id}/certificates`),
                    api.get(`/api/plants/${id}/generation/hourly?date=${selectedDate}`)
                ]);
                setPlant(plantRes.data);
                setCertificates(certRes.data || []);
                setHourlyData(hourlyRes.data || []);

                if (role === 'admin' || role === 'expert') {
                    const rawRes = await api.get(`/api/plants/${id}/generation/latest?limit=20`);
                    setRawRecords(rawRes.data || []);
                }
                
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError('Failed to load plant profile. It may not exist.');
                setLoading(false);
            }
        };
        fetchProfile();
    }, [id, selectedDate]);

    if (loading) return <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center text-emerald-500 font-bold uppercase tracking-widest text-sm"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mr-3"></div>Loading Profile...</div>;
    if (error) return <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center text-rose-500 font-bold">{error}</div>;
    if (!plant) return null;

    // Chart logic
    const labels = hourlyData.map(d => `${d.hour}:00`);
    const lineChartData = {
        labels,
        datasets: [
            {
                label: 'kWh Produced',
                data: hourlyData.map(d => d.total_kwh),
                borderColor: 'rgba(16, 185, 129, 1)',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 3,
                pointBackgroundColor: 'rgba(16, 185, 129, 1)'
            }
        ]
    };

    const getBackPath = () => {
        if (!userRole) return '/';
        switch (userRole) {
            case 'admin': return '/admin';
            case 'expert': return '/dashboard/expert';
            case 'investor': return '/dashboard/investor';
            case 'observer': return '/dashboard/observer';
            default: return '/dashboard';
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0c12] text-slate-200 p-4 sm:p-8 font-sans selection:bg-emerald-500/30 pb-20">
            <header className="max-w-5xl mx-auto mb-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <Link to={getBackPath()} className="text-emerald-500 hover:text-emerald-400 font-bold text-sm uppercase tracking-widest flex items-center gap-2 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                    Back to Platform
                </Link>
                <div className="bg-emerald-500/10 text-emerald-400 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-500/20">Public Registry</div>
            </header>

            <main className="max-w-5xl mx-auto space-y-10">
                {/* Profile Header */}
                <div className="glass p-6 sm:p-10 rounded-xl border border-white/5 relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 p-10 opacity-5">
                        <svg className="w-64 h-64" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 22h20L12 2zm0 4.5l6.5 13.5h-13L12 6.5z"/></svg>
                    </div>
                    <div className="relative z-10 flex flex-col md:flex-row gap-8 md:gap-10 items-start md:items-center">
                        <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 shrink-0">
                            <span className="text-4xl font-black text-white">{plant.plant_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1">
                            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">{plant.plant_name}</h1>
                            <p className="text-slate-400 flex items-center gap-2 text-sm"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg> {plant.location}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-left md:text-right w-full md:w-auto">
                            <div><p className="text-[10px] uppercase font-black tracking-widest text-slate-500">Capacity</p><p className="font-bold text-emerald-400 text-lg">{plant.capacity_mw} MW</p></div>
                            <div><p className="text-[10px] uppercase font-black tracking-widest text-slate-500">Type</p><p className="font-bold text-white capitalize">{plant.plant_type}</p></div>
                            <div><p className="text-[10px] uppercase font-black tracking-widest text-slate-500">Owner</p><p className="font-bold text-white">{plant.owner_name}</p></div>
                            <div><p className="text-[10px] uppercase font-black tracking-widest text-slate-500">Commissioned</p><p className="font-bold text-white">{new Date(plant.commissioning_date).getFullYear()}</p></div>
                        </div>
                    </div>
                </div>

                {/* Onboarding & Issuance Pipeline */}
                <div className="glass p-8 rounded-xl border border-white/5 shadow-2xl">
                    <h2 className="text-xl font-bold text-white mb-2 px-2">I-REC Onboarding & Issuance Pipeline</h2>
                    <PipelineStatus plant={plant} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* Devices */}
                    <div className="glass p-8 rounded-xl border border-white/5 space-y-6">
                        <h2 className="text-xl font-bold text-white">Monitored Devices</h2>
                        <div className="space-y-3">
                            {plant.devices && plant.devices.length > 0 ? plant.devices.map(d => (
                                <div key={d.id} className="bg-black/40 p-5 rounded-xl border border-white/5 flex justify-between items-center hover:border-emerald-500/20 transition-colors">
                                    <div>
                                        <p className="font-bold text-slate-200">{d.device_name}</p>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">{d.device_type}</p>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${d.tamper_status ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-500'}`}>{d.tamper_status ? 'Tampered' : 'Secure'}</span>
                                        <p className="text-[10px] text-slate-500 mt-2">Seen: {d.last_seen ? new Date(d.last_seen).toLocaleDateString() : 'N/A'}</p>
                                    </div>
                                </div>
                            )) : <p className="text-slate-500 text-sm italic">No devices publicly listed.</p>}
                        </div>
                    </div>

                    {/* Chart */}
                    <div className="glass p-6 sm:p-8 rounded-xl border border-white/5">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h2 className="text-xl font-bold text-white">Hourly Production (kWh)</h2>
                            <input 
                                type="date" 
                                value={selectedDate} 
                                onChange={e => setSelectedDate(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-500 w-full sm:w-auto"
                            />
                        </div>
                        <div className="h-64">
                            <Line 
                                data={lineChartData} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: {
                                        y: { beginAtZero: true, grid: { color: 'rgba(255,255,255,0.05)' } },
                                        x: { grid: { display: false } }
                                    }
                                }} 
                            />
                        </div>
                    </div>
                </div>

                {/* Raw Records Table (Admin/Expert Only) */}
                {(userRole === 'admin' || userRole === 'expert') && (
                    <div className="glass p-8 rounded-xl border border-white/5">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-emerald-400">Raw Sensor Telemetry (Admin/Expert View)</h2>
                            <span className="text-xs bg-emerald-500/10 text-emerald-500 px-3 py-1 rounded-full uppercase tracking-widest font-black">Latest 20 Readings</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-white/5">
                                    <tr>
                                        <th className="pb-4">Timestamp</th>
                                        <th className="pb-4">Device ID</th>
                                        <th className="pb-4 text-right">Increment (kWh)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {rawRecords.map((r, i) => (
                                        <tr key={i} className="hover:bg-white/5 transition-colors">
                                            <td className="py-3 text-xs text-slate-300 font-mono">{new Date(r.timestamp).toLocaleString()}</td>
                                            <td className="py-3 text-xs text-slate-400">{r.device_id || 'Unknown'}</td>
                                            <td className="py-3 text-sm font-bold text-emerald-400 text-right">{Number(r.increment_kwh).toFixed(4)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {rawRecords.length === 0 && <p className="text-center text-slate-500 py-6 text-sm">No recent telemetry records found.</p>}
                        </div>
                    </div>
                )}

                {/* Certificates */}
                <div className="glass p-8 rounded-xl border border-white/5">
                    <h2 className="text-xl font-bold text-white mb-6">Issued Certificates</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-white/5">
                                <tr>
                                    <th className="pb-4">Attestation ID</th>
                                    <th className="pb-4">Type</th>
                                    <th className="pb-4">Timestamp</th>
                                    <th className="pb-4 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {certificates.map((c, i) => (
                                    <tr key={i} className="hover:bg-white/5 transition-colors">
                                        <td className="py-4 font-mono text-[10px] text-slate-300">
                                            <Link to="/verify" className="hover:text-emerald-400 hover:underline">{c.id || c.attestation_id}</Link>
                                        </td>
                                        <td className="py-4 text-xs font-bold text-emerald-500">{c.isPhysical ? 'Physical' : 'Energy'}</td>
                                        <td className="py-4 text-xs text-slate-400">{new Date(c.timestamp).toLocaleDateString()}</td>
                                        <td className="py-4 text-right">
                                            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-slate-800 text-slate-300">{c.status || 'Active'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {certificates.length === 0 && <p className="text-center text-slate-500 py-10 text-sm">No certificates issued from this plant yet.</p>}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PublicPlantProfile;
